"use client";

import { c as _c } from "react/compiler-runtime";
import { Alert, BodyLong, Modal, VStack } from "@navikt/ds-react";
import { useParams, useRouter } from "next/navigation";
import { startTransition, useActionState, useState, useTransition } from "react";
import { knappKlikket } from "@/common/analytics/events-and-properties/knappKlikket-properties";
import { logAnalyticsEvent } from "@/common/analytics/logAnalyticsEvent";
import { getAGOpprettNyPlanHref } from "@/common/route-hrefs";
import { slettUtkastAndRedirectToNyPlanServerAction } from "@/server/actions/slettUtkast";
import { upsertUtkastWithAktivPlanServerAction } from "@/server/actions/upsertUtkastWithAktivPlan";
import { FetchErrorAlert } from "@/ui/FetchErrorAlert";
import { TrackedButton } from "@/ui/TrackedButton";
interface Props {
  ref: React.RefObject<HTMLDialogElement | null>;
  hasUtkast: boolean;
}
export function LagNyPlanModal(t0) {
  const $ = _c(38);
  const {
    ref,
    hasUtkast
  } = t0;
  const router = useRouter();
  const {
    narmesteLederId
  } = useParams();
  const [lastAction, setLastAction] = useState(null);
  const [isPendingPush, startPushTransition] = useTransition();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      error: null
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [t2, upsertUtkastAction, isPendingUpsert] = useActionState(upsertUtkastWithAktivPlanServerAction, t1);
  const {
    error: upsertError
  } = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      error: null
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [t4, slettUtkastAction, isPendingSlett] = useActionState(slettUtkastAndRedirectToNyPlanServerAction, t3);
  const {
    error: slettError
  } = t4;
  const isPending = isPendingUpsert || isPendingSlett || isPendingPush;
  const error = lastAction === "upsert" ? upsertError : lastAction === "slett" ? slettError : null;
  let t5;
  if ($[2] !== hasUtkast || $[3] !== narmesteLederId || $[4] !== router) {
    t5 = function handleBegynnMedTomPlanClick() {
      setLastAction("slett");
      if (hasUtkast) {
        startTransition(() => {
          slettUtkastAction(narmesteLederId);
        });
        return;
      }
      startPushTransition(() => {
        router.push(getAGOpprettNyPlanHref(narmesteLederId));
      });
    };
    $[2] = hasUtkast;
    $[3] = narmesteLederId;
    $[4] = router;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleBegynnMedTomPlanClick = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      heading: "Lag ny oppf\xF8lgingsplan"
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = !isPending;
  let t8;
  if ($[7] !== isPending) {
    t8 = () => !isPending;
    $[7] = isPending;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <BodyLong>Du kan ta utgangspunkt i den forrige planen, eller begynne på nytt.</BodyLong>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== hasUtkast) {
    t10 = hasUtkast && <Alert variant="warning">Du har allerede et påbegynt utkast. Hvis du fortsetter, blir utkastet erstattet.</Alert>;
    $[10] = hasUtkast;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== error) {
    t11 = <FetchErrorAlert error={error} />;
    $[12] = error;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10 || $[15] !== t11) {
    t12 = <Modal.Body><VStack gap="space-16">{t9}{t10}{t11}</VStack></Modal.Body>;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const t13 = hasUtkast ? isPendingSlett : isPendingPush;
  let t14;
  if ($[17] !== handleBegynnMedTomPlanClick || $[18] !== isPending || $[19] !== t13) {
    t14 = <TrackedButton variant="secondary" onClick={handleBegynnMedTomPlanClick} loading={t13} disabled={isPending} tracking={knappKlikket.aktivPlanSide.lagNyPlanModal.begynnMedTomPlan}>Begynn med tom plan</TrackedButton>;
    $[17] = handleBegynnMedTomPlanClick;
    $[18] = isPending;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== narmesteLederId) {
    t15 = () => {
      setLastAction("upsert");
      upsertUtkastAction(narmesteLederId);
    };
    $[21] = narmesteLederId;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== isPending || $[24] !== isPendingUpsert) {
    t16 = <TrackedButton type="submit" variant="primary" loading={isPendingUpsert} disabled={isPending} tracking={knappKlikket.aktivPlanSide.lagNyPlanModal.begynnMedInnholdFraForrigePlan}>Begynn med innhold fra forrige plan</TrackedButton>;
    $[23] = isPending;
    $[24] = isPendingUpsert;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t15 || $[27] !== t16) {
    t17 = <form action={t15}>{t16}</form>;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t14 || $[30] !== t17) {
    t18 = <Modal.Footer>{t14}{t17}</Modal.Footer>;
    $[29] = t14;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== ref || $[33] !== t12 || $[34] !== t18 || $[35] !== t7 || $[36] !== t8) {
    t19 = <Modal ref={ref} header={t6} closeOnBackdropClick={t7} onBeforeClose={t8} onClose={_temp}>{t12}{t18}</Modal>;
    $[32] = ref;
    $[33] = t12;
    $[34] = t18;
    $[35] = t7;
    $[36] = t8;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  return t19;
}
function _temp() {
  logAnalyticsEvent({
    name: "knapp klikket",
    properties: {
      ...knappKlikket.aktivPlanSide.lagNyPlanModal.avbryt
    }
  });
}